var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import React, { useState, useRef, useEffect, useContext } from 'react';
import { ModalHeader, ModalBody, ModalFooter, LoadingButton, Typography, FormStatusMessage, FormLabel, Input, FormController, Button, } from '@one-thd/sui-atomic-components';
import { ExperienceContext } from '@thd-nucleus/experience-context';
import { yupResolver } from '@hookform/resolvers/yup';
import { Controller, FormProvider, useForm, } from 'react-hook-form';
import ReCAPTCHA from 'react-google-recaptcha';
import { GiftCardLogoAndNumber } from '../gift-card/logo-number';
import { validationSchema } from './validations';
import { getTextFieldStatus } from '../../../utils/form';
import { InfoPopover } from '../../shared/popover/popover';
import { GIFT_CARD_BACKSIDE_IMG_URL } from '../../../constants/popover-content';
import { getSiteKey } from '../../../utils/general';
export const CheckGiftCardBalanceModal = ({ card, isLoading, wrongPin, hideModal, requestGiftCardBalance, }) => {
    var _a;
    const { channel } = useContext(ExperienceContext);
    const [isPinIncorrect, setIsPinIncorrect] = useState(false);
    const isMobile = channel === 'mobile';
    const iconSize = isMobile ? 'small' : 'regular';
    const ReCAPTCHARef = useRef(null);
    const defaultValues = {
        pin: '',
        recaptcha: '',
    };
    const formMethods = useForm({
        defaultValues,
        resolver: yupResolver(validationSchema),
        mode: 'onTouched',
    });
    const { control, formState: { errors: formErrors }, handleSubmit, resetField, } = formMethods;
    const resetReCaptcha = () => {
        var _a;
        resetField('recaptcha');
        (_a = ReCAPTCHARef.current) === null || _a === void 0 ? void 0 : _a.reset();
    };
    useEffect(() => {
        if (wrongPin) {
            setIsPinIncorrect(true);
        }
    }, [wrongPin]);
    const onSubmitForm = ({ pin, recaptcha, }) => __awaiter(void 0, void 0, void 0, function* () {
        requestGiftCardBalance(pin, recaptcha);
        resetReCaptcha();
    });
    const popoverContent = {
        title: '',
        message: (React.createElement(React.Fragment, null,
            React.createElement("img", { src: GIFT_CARD_BACKSIDE_IMG_URL, width: "375", height: "187", alt: "Back of Gift Card" }),
            React.createElement(Typography, { variant: "body-xs" }, "This is your 4-digit unique identifier"))),
    };
    return (React.createElement(FormProvider, Object.assign({}, formMethods),
        React.createElement("form", { onSubmit: handleSubmit(onSubmitForm) },
            React.createElement(ModalHeader, { onClose: hideModal }, "Check Gift Card Balance"),
            React.createElement(ModalBody, null,
                React.createElement("div", { className: "sui-flex sui-flex-col sui-gap-y-6 sui-mb-5" },
                    React.createElement("div", { className: "sui-flex sui-flex-wrap" },
                        React.createElement(GiftCardLogoAndNumber, { card: card, shouldShowInactive: true })),
                    React.createElement(Controller, { name: "pin", control: control, render: (_a) => {
                            var _b;
                            var _c = _a.field, { ref, onChange } = _c, rest = __rest(_c, ["ref", "onChange"]);
                            return (React.createElement(FormController, null,
                                React.createElement(FormLabel, { htmlFor: "pin" },
                                    "Card PIN*",
                                    popoverContent && (React.createElement(InfoPopover, { iconSize: iconSize, title: popoverContent.title, placement: isMobile ? 'bottom' : 'right' },
                                        React.createElement(Typography, { variant: "body-base" }, popoverContent.message)))),
                                React.createElement(Input, Object.assign({}, rest, { id: "pin", fullWidth: true, type: "text", status: getTextFieldStatus(formErrors.pin), onChange: (event) => {
                                        if (isPinIncorrect) {
                                            setIsPinIncorrect(false);
                                        }
                                        onChange(event);
                                    }, inputProps: {
                                        'data-testid': 'pin',
                                        maxLength: 4,
                                    } })),
                                React.createElement(FormStatusMessage, { status: "error" }, (_b = formErrors.pin) === null || _b === void 0 ? void 0 : _b.message),
                                isPinIncorrect && (React.createElement(FormStatusMessage, { status: "error" }, "We don't recognize this PIN. Review the card details and try again."))));
                        } }),
                    React.createElement("div", null,
                        React.createElement(Controller, { name: "recaptcha", control: control, render: ({ field: { onChange } }) => (React.createElement(ReCAPTCHA, { ref: ReCAPTCHARef, sitekey: getSiteKey(true), onChange: onChange, size: isMobile ? 'compact' : 'normal', "data-testid": "recaptcha" })) }),
                        React.createElement(FormStatusMessage, { status: "error" }, (_a = formErrors.recaptcha) === null || _a === void 0 ? void 0 : _a.message)))),
            React.createElement(ModalFooter, { position: isMobile ? 'center' : 'end' },
                React.createElement("div", { className: "sui-flex sui-p-2 sui-gap-4 sui-flex-col-reverse sui-w-full sm:sui-flex-row sm:sui-w-auto" },
                    React.createElement(Button, { variant: "secondary", onClick: hideModal }, "Cancel"),
                    React.createElement(LoadingButton, { loading: isLoading, variant: "primary", type: "submit", "data-testid": "submitBtn" }, "Check Card Balance"))))));
};
